import { Modal, Table, Typography, Select, Row, Col, Space } from "antd";
import { useQuery } from "react-query";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IBaselinesService } from "@shared/interfaces/baselines-service.interface";
import { BaselineDto } from "@shared/types/baselines/baselines.dto";
import { TierDto } from "@shared/types/tiers/tiers.dto";
import { filterAndSortBaselines } from "@shared/utils/baselines-view";
import { useMemo, useState } from "react";

interface ManageBaselinesModalProps {
  open: boolean;
  tier: TierDto | null;
  onClose: () => void;
}

const ManageBaselinesModal = ({
  open,
  tier,
  onClose,
}: ManageBaselinesModalProps) => {
  const baselinesService = appInject<IBaselinesService>(
    DI_TOKENS.baselinesService,
  );
  const [viewMode, setViewMode] = useState<"positive" | "negative">("positive");

  // List all logical baselines (Global)
  const { data: baselines = [], isLoading } = useQuery(
    ["baselines-global"],
    () => baselinesService.list(),
    { enabled: open, keepPreviousData: true },
  );

  const filteredBaselines = useMemo(
    () => filterAndSortBaselines(baselines, viewMode, 0),
    [baselines, viewMode],
  );

  const columns = [
    {
      title: "Required Score",
      key: "calculated",
      render: (_: any, record: BaselineDto) => {
        if (!tier) return "-";
        const score = record.draftGrade + tier.gloThreshold;
        return <Space>{score}</Space>;
      },
    },
    {
      title: "Percentile",
      dataIndex: "percentile",
      render: (val: number | null | undefined) =>
        val == null || Number.isNaN(Number(val))
          ? "-"
          : `${Number(val).toFixed(2)}%`,
    },
    {
      title: "GLO Change",
      dataIndex: "gloChange",
      render: (val: number | null | undefined) => {
        if (val === null || val === undefined || Number.isNaN(Number(val))) {
          return (
            <span style={{ color: "black", fontWeight: "bold" }}>—</span>
          );
        }
        const n = Number(val);
        return (
          <span
            style={{
              color: n > 0 ? "green" : n < 0 ? "red" : "black",
              fontWeight: "bold",
            }}
          >
            {n > 0 ? `+${n}` : n}
          </span>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      title={null} // Custom title in Row
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{ marginRight: 30, marginBottom: 10 }}
      >
        <Col>
          <Typography.Title
            level={4}
            style={{
              margin: 0,
            }}
          >
            Calculated Baselines for {tier?.name || "Tier"}
          </Typography.Title>
        </Col>
        <Col>
          <Select
            value={viewMode}
            onChange={(val) => setViewMode(val)}
            style={{ width: 220 }}
          >
            <Select.Option value="positive">Positives</Select.Option>
            <Select.Option value="negative">Negatives</Select.Option>
          </Select>
        </Col>
      </Row>

      <Typography.Text
        type="secondary"
        style={{ display: "block", marginBottom: 16 }}
      >
        These are the independent global baselines. The &quot;Required Score&quot; shows
        what a user in this Tier needs to score to achieve the corresponding
        Draft Grade effect.
      </Typography.Text>

      <Table
        rowKey="id"
        dataSource={filteredBaselines}
        columns={columns}
        loading={isLoading}
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default ManageBaselinesModal;
